import React from 'react';

import { CaretDownFilled, CaretUpFilled, QuestionCircleOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { theme } from 'theme';

import { PositiveNumberInput } from 'pages/purchasing/PlanningPage/modals/PositiveNumberInput';

import { FlexSpace } from 'components/FlexSpace';
import { FlexSpacer } from 'components/FlexSpacer';
import { InputValidation } from 'components/Input/InputValidation';
import { Tooltip } from 'components/Tooltip';
import { Typography } from 'components/Typography';

import { formatPercent, getChangeColor } from 'utils/formatting';

import { DailyWorkflowCardBase } from './DailyWorkflowCardBase';
import { ALLOW_KEYBOARD_SHORTCUTS_ID } from './KeyboardShortcut';

export type DailyWorkflowValueCardProps = {
  title: string;
  currentValue: number;
  targetValue?: number;
  description?: string;
  subText?: string;
  isEditable?: boolean;
  keyboardShortcut?: string;
  showChange?: boolean;
  onChange?: (value: number) => void;
  onKeyboardShortcutClick?: () => void;
  formatFn?: (value: number) => string | React.ReactNode;
  inputId?: string;
  validate?: (value: number) => string | boolean;
  flexGrow?: number;
  headerActions?: React.ReactNode;
  useFlatDifference?: boolean;
};

const MAX_INPUT_VALUE = 999999999;

export const DailyWorkflowValueCard = ({
  title,
  currentValue,
  targetValue,
  description,
  subText,
  isEditable,
  keyboardShortcut,
  showChange,
  onChange,
  onKeyboardShortcutClick,
  formatFn,
  inputId,
  validate,
  flexGrow,
  headerActions,
  useFlatDifference = false,
}: DailyWorkflowValueCardProps) => {
  const delta = targetValue ? targetValue - currentValue : 0;
  const deltaPercentage = targetValue && currentValue !== 0 ? delta / currentValue : 0;
  const deltaDisplay = useFlatDifference ? delta : deltaPercentage;

  return (
    <DailyWorkflowCardBase
      title={title}
      keyboardShortcut={keyboardShortcut}
      onKeyboardShortcutClick={onKeyboardShortcutClick}
      headerActions={
        headerActions || (
          <>
            {description && (
              <Tooltip title={description}>
                <QuestionCircleOutlined />
              </Tooltip>
            )}
          </>
        )
      }
      flexGrow={flexGrow}
    >
      <FlexSpace
        direction="row"
        justify="space-between"
        className={css`
          min-height: 40px;
          width: 100%;
        `}
      >
        <Typography style={{ fontSize: 16, fontWeight: 700 }}>
          {formatFn ? formatFn(currentValue) : currentValue}
        </Typography>
        {targetValue !== undefined && (
          <>
            <Typography style={{ fontSize: 16, fontWeight: 700 }}>{'->'}</Typography>
            {isEditable ? (
              <InputValidation validate={() => validate?.(targetValue)}>
                <PositiveNumberInput
                  value={targetValue}
                  onChange={(value) => {
                    // Prevent inputting a value greater than MAX_INPUT_VALUE
                    if (value && value > MAX_INPUT_VALUE) {
                      return;
                    }
                    onChange?.(value || 0);
                  }}
                  className={`${css`
                    min-width: 80px;
                    max-width: 120px;
                    font-size: 16px;
                    font-weight: 700;
                    color: ${theme.colors.primary[600]};
                  `} ${keyboardShortcut ? ALLOW_KEYBOARD_SHORTCUTS_ID : ''}`}
                  id={inputId}
                />
              </InputValidation>
            ) : (
              <Typography style={{ fontSize: 16, fontWeight: 700 }}>
                {formatFn ? formatFn(targetValue) : targetValue}
              </Typography>
            )}
          </>
        )}
        <FlexSpacer />
        {showChange && (
          <p
            className={css`
              font-size: ${deltaDisplay > 1000 ? '10px' : '12px'};
              margin: 0%;
              font-weight: 700;
              justify-content: flex-end;
              width: 40px;
              display: flex;
              align-items: center;
              gap: 4px;
            `}
          >
            {currentValue === 0 ? '-%' : formatPercent(deltaDisplay, 0)}
            {!delta ? (
              <CaretUpFilled style={{ color: getChangeColor(1) }} />
            ) : delta > 0 ? (
              <CaretUpFilled style={{ color: getChangeColor(delta) }} />
            ) : (
              <CaretDownFilled style={{ color: getChangeColor(delta) }} />
            )}
          </p>
        )}
      </FlexSpace>
      {subText ? <Typography style={{ fontSize: 10 }}>{subText}</Typography> : <div style={{ height: 16 }} />}
    </DailyWorkflowCardBase>
  );
};
